import { Box, Chip, Divider, List, ListItem, ListItemText, Paper, Typography } from '@mui/material';

import type { NormalizedModuleMap, SelectionKey } from '../../types/merge-customization';

interface MergePreviewPanelProps {
  modules: NormalizedModuleMap;
  selections: Set<SelectionKey>;
  primaryPatientName: string;
}

export function MergePreviewPanel({
  modules,
  selections,
  primaryPatientName,
}: MergePreviewPanelProps) {
  const moduleEntries = Object.entries(modules)
    .map(([key, mod]) => {
      const selectedItems = mod.items.filter(i => selections.has(i.selectionKey));
      return { key, mod, selectedItems };
    })
    .filter(entry => entry.selectedItems.length > 0);

  const totalSelected = moduleEntries.reduce((sum, e) => sum + e.selectedItems.length, 0);

  return (
    <Paper
      variant="outlined"
      sx={{
        position: 'sticky',
        top: 24,
        maxHeight: 'calc(100vh - 180px)',
        overflow: 'auto',
      }}
    >
      <Box
        sx={{
          p: 2,
          borderBottom: 1,
          borderColor: 'divider',
          backgroundColor: 'primary.50',
        }}
      >
        <Typography variant="subtitle2" fontWeight={600}>
          Merged Result Preview
        </Typography>
        <Typography variant="caption" color="text.secondary">
          {primaryPatientName} will have {totalSelected} item{totalSelected !== 1 ? 's' : ''}
        </Typography>
      </Box>

      {moduleEntries.length === 0 ? (
        <Box sx={{ p: 3, textAlign: 'center' }}>
          <Typography variant="body2" color="text.secondary">
            No items selected
          </Typography>
        </Box>
      ) : (
        moduleEntries.map(({ key, mod, selectedItems }, idx) => (
          <Box key={key}>
            {idx > 0 && <Divider />}
            <Box
              sx={{
                px: 2,
                py: 1,
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                backgroundColor: 'grey.50',
              }}
            >
              <Typography variant="caption" fontWeight={600}>
                {mod.name}
              </Typography>
              <Chip
                label={selectedItems.length}
                size="small"
                sx={{ height: 18, fontSize: '0.7rem' }}
              />
            </Box>
            <List dense disablePadding>
              {selectedItems.map(item => (
                <ListItem key={item.selectionKey} sx={{ py: 0.25, px: 2 }}>
                  <ListItemText
                    primary={item.displayLabel}
                    primaryTypographyProps={{ variant: 'body2', fontSize: '0.8rem' }}
                  />
                </ListItem>
              ))}
            </List>
          </Box>
        ))
      )}
    </Paper>
  );
}
